import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BookHeart, ChevronLeft, ChevronRight, Loader2, Sparkles, Save, Plus, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { subjectStyle, ukToday, addDays, fmtDue, plusDays } from "@/lib/subjects";
import { VoiceButton } from "@/components/VoiceButton";
import { AddSubject } from "@/components/AddSubject";
import { W } from "@/lib/edition";

const LessonPrompt = ({ les, i, value, onChange }) => (
  <li data-testid={`daylog-lesson-${i}`} className="card p-4 sm:p-5">
    <div className="flex items-center gap-3">
      <span className="w-12 shrink-0 font-mono text-sm text-slate-600">{les.start || "Extra"}</span>
      <span className="w-1.5 h-8 rounded-full" style={{ backgroundColor: subjectStyle(les.subject).dot }} />
      <Link to={`/daylog/subject/${encodeURIComponent(les.subject)}`} data-testid={`daylog-subject-link-${i}`} className="flex-1 min-w-0 font-semibold text-white truncate hover:text-gold-300">{les.subject}{les.room && <span className="font-normal text-slate-500"> · {les.room}</span>}</Link>
      {value.trim() && <Check className="w-4 h-4 text-gold-300" />}
    </div>
    <div className="mt-3 flex items-center justify-between gap-2">
      <label className="block text-sm text-slate-600" htmlFor={`daylog-answer-${i}`}>What did you do in {les.subject}?</label>
      <VoiceButton id={`daylog-voice-${i}`} hint={les.subject} onText={(t) => onChange(value.trim() ? `${value.trim()} ${t}` : t)} />
    </div>
    <textarea id={`daylog-answer-${i}`} data-testid={`daylog-answer-${i}`} value={value} onChange={(e) => onChange(e.target.value)} rows={2} maxLength={2000}
      placeholder="e.g. Started rates of reaction practical, need to finish the graph"
      className="mt-1.5 w-full rounded-2xl border border-slate-200 bg-surface-deep/60 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none focus:border-gold-400" />
  </li>
);

const Summary = ({ d, onAddTask }) => (
  <section data-testid="daylog-summary" className="card p-5 sm:p-6 !border-gold-400/50 animate-rise">
    <p className="eyebrow flex items-center gap-2"><Sparkles className="w-4 h-4" />Your day</p>
    <p data-testid="daylog-summary-text" className="mt-2 text-slate-800 leading-relaxed">{d.summary}</p>
    {d.follow_ups.length > 0 && (
      <>
        <p className="mt-4 eyebrow !text-rose-600">To follow up</p>
        <ul className="mt-2 space-y-2">
          {d.follow_ups.map((f, i) => (
            <li key={i} data-testid={`daylog-followup-${i}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-surface-2 px-3 py-2 text-sm">
              <span className="flex-1 text-white">{f}</span>
              <button data-testid={`daylog-followup-add-${i}`} onClick={() => onAddTask(f)} className="inline-flex items-center gap-1 rounded-full border border-gold-400/60 px-2.5 py-1 text-xs font-semibold text-gold-300 hover:bg-gold-400 hover:text-black"><Plus className="w-3 h-3" />Homework</button>
            </li>
          ))}
        </ul>
      </>
    )}
  </section>
);

export default function DayLog() {
  const [day, setDay] = useState(ukToday());
  const [d, setD] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState("");
  const [past, setPast] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [extras, setExtras] = useState([]);
  const dirty = useRef(false);
  const loadPast = () => {
    api.get("/daylog/list").then((r) => setPast(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/daylog/subjects").then((r) => setSubjects(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useEffect(() => {
    setD(null);
    api.get("/daylog", { params: { day } }).then((r) => {
      const data = r.data ?? null;
      setD(data);
      setAnswers(Object.fromEntries((data?.entries ?? []).map((e) => [e.key, e.answer])));
      const keys = new Set((data?.lessons ?? []).map((l) => l.key));
      setExtras((data?.entries ?? []).filter((e) => !keys.has(e.key)).map((e) => ({ key: e.key, subject: e.subject, start: e.start || "" })));
      dirty.current = false;
    }).catch(() => setD(false));
  }, [day]);
  useEffect(() => { loadPast(); }, []);
  const lessons = [...(Array.isArray(d?.lessons) ? d.lessons : []), ...extras];
  const addExtra = (s) => {
    const key = `extra-${s.toLowerCase()}`.slice(0, 80);
    if (lessons.some((l) => l.key === key)) { toast(`${s} is already on today's log`); return; }
    setExtras((x) => [...x, { key, subject: s, start: "" }]);
  };
  const save = async () => {
    const entries = lessons.map((l) => ({ key: l.key, subject: l.subject, start: l.start || "", answer: answers[l.key] || "" }));
    await api.put("/daylog", { day, entries });
    dirty.current = false;
    loadPast();
  };
  const onSave = async () => { setBusy("save"); try { await save(); toast.success(`Saved your log for ${fmtDue(day)}`); } catch (e) { toast.error(formatApiError(e)); } setBusy(""); };
  const onSummary = async () => {
    setBusy("ai");
    try { await save(); const { data } = await api.post("/daylog/summary", { day }); setD((x) => ({ ...x, ...data })); loadPast(); } catch (e) { toast.error(formatApiError(e)); }
    setBusy("");
  };
  const addTask = async (title) => {
    try { await api.post("/homework", { title: title.slice(0, 200), subject: "", due: plusDays(1), kind: "homework" }); toast.success("Added to homework, due tomorrow"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const filled = lessons.filter((l) => (answers[l.key] || "").trim()).length;
  return (
    <main data-testid="daylog-page" className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow flex items-center gap-2"><BookHeart className="w-4 h-4" /><W s="Day log" c="Work log" /></p>
          <h1 data-testid="daylog-title" className="mt-2 font-display text-4xl sm:text-5xl text-white leading-none">{day === ukToday() ? "Today" : fmtDue(day)}</h1></div>
        <div className="flex items-center gap-2">
          <button data-testid="daylog-prev" onClick={() => setDay(addDays(day, -1))} aria-label="Previous day" className="grid place-items-center w-10 h-10 rounded-full border border-slate-200 text-slate-700"><ChevronLeft className="w-4 h-4" /></button>
          <input data-testid="daylog-date" type="date" max={ukToday()} value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="h-10 rounded-full border border-slate-200 bg-surface px-3 text-sm text-white" />
          <button data-testid="daylog-next" onClick={() => setDay(addDays(day, 1))} disabled={day >= ukToday()} aria-label="Next day" className="grid place-items-center w-10 h-10 rounded-full border border-slate-200 text-slate-700 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      {d === null && <div className="h-48 rounded-3xl bg-surface animate-pulse" />}
      {d === false && <p className="text-slate-500">Couldn't load this day.</p>}
      {d && !lessons.length && <p data-testid="daylog-no-lessons" className="rounded-3xl border-2 border-dashed border-slate-200 p-8 text-center text-slate-500">{d.closed ? `${d.closed}.` : <W s="No lessons on this day." c="No meetings on this day." />} You can still log what you studied below.</p>}
      {lessons.length > 0 && (
        <>
          <p className="text-sm text-slate-600"><W s="Write a line or two about each lesson." c="Write a line or two about each meeting." /> {filled}/{lessons.length} done.</p>
          <ul className="space-y-3">{lessons.map((l, i) => <LessonPrompt key={l.key} les={l} i={i} value={answers[l.key] || ""} onChange={(v) => { dirty.current = true; setAnswers((a) => ({ ...a, [l.key]: v })); }} />)}</ul>
          <div className="flex flex-wrap gap-2">
            <button data-testid="daylog-save" onClick={onSave} disabled={Boolean(busy)} className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-800 hover:text-white disabled:opacity-50">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save</button>
            <button data-testid="daylog-summarise" onClick={onSummary} disabled={Boolean(busy) || !filled} className="inline-flex items-center gap-2 rounded-full bg-gold-400 px-5 py-3 text-sm font-bold text-black hover:bg-gold-300 shadow-gold disabled:opacity-40">{busy === "ai" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{d.summary ? "Save and redo summary" : "Save and summarise my day"}</button>
          </div>
        </>
      )}
      {d && <AddSubject id="daylog-add-subject" onAdd={addExtra} label={lessons.length ? <W s="Studied something else? Add a subject" c="Worked on something else? Add a project" /> : <W s="Log a subject you studied" c="Log a project you worked on" />} />}
      {d?.summary && <Summary d={{ summary: d.summary, follow_ups: Array.isArray(d.follow_ups) ? d.follow_ups : [] }} onAddTask={addTask} />}
      {subjects.length > 0 && (
        <section data-testid="daylog-subjects">
          <p className="eyebrow"><W s="Subject timelines" c="Project timelines" /></p>
          <div className="mt-2 flex flex-wrap gap-2">
            {subjects.map((s) => (
              <Link key={s.subject} to={`/daylog/subject/${encodeURIComponent(s.subject)}`} data-testid={`daylog-subject-chip-${s.subject.toLowerCase().replace(/\s+/g, "-")}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors hover:border-gold-400 ${subjectStyle(s.subject).chip}`}>
                {s.subject}<span className="text-xs opacity-75">{s.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
      {past.length > 0 && (
        <section>
          <p className="eyebrow">Past logs</p>
          <ul className="mt-2 space-y-2">
            {past.slice(0, 30).map((p) => (
              <li key={p.date}><button data-testid={`daylog-past-${p.date}`} onClick={() => { setDay(p.date); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className={`card card-hover w-full text-left !rounded-2xl p-3.5 ${p.date === day ? "!border-gold-400" : ""}`}>
                <span className="flex items-center gap-2"><b className="text-white">{fmtDue(p.date)}</b><span className="text-xs text-slate-500">{p.count} lesson{p.count === 1 ? "" : "s"} logged</span></span>
                {p.summary && <span className="mt-1 block text-sm text-slate-600 line-clamp-2">{p.summary}</span>}
              </button></li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
